import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "../ui/input";
import { BusyButton } from "../ui/BusyButton";
import { Label } from "../ui/label";

interface LoginFormProps {
  onSubmit: (nombreUsuario: string, password: string) => void;
  isLoading: boolean;
  error?: string | null;
}

const LoginForm: React.FC<LoginFormProps> = ({
  onSubmit,
  isLoading,
  error,
}) => {
  const [nombreUsuario, setNombreUsuario] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(nombreUsuario, password);
  };

  const isFormValid =
    nombreUsuario.trim().length > 0 && password.trim().length > 0;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-4">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-t-2 border-primary" />
        <span className="font-semibold text-primary">
          Cargando, iniciando sesión...
        </span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 min-w-[25rem]">
      <div>
        <Label htmlFor="nombreUsuario" className="text-[16px]">
          Nombre usuario<span className="text-destructive ml-1">*</span>
        </Label>
        <Input
          id="nombreUsuario"
          type="text"
          value={nombreUsuario}
          onChange={(e) => setNombreUsuario(e.target.value)}
          required
        />
      </div>

      <div>
        <Label htmlFor="password" className="text-[16px]">
          Contraseña<span className="text-destructive ml-1">*</span>
        </Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button
            type="button"
            aria-label={
              showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
            }
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 transition-colors hover:text-gray-700"
            onClick={() => setShowPassword((prev) => !prev)}
            tabIndex={-1}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-500">
          {error}
        </p>
      )}

      <div className="mt-10 text-center text-sm text-[var(--foreground)]">
        <span>
          Al dar clic en "Iniciar sesión" aceptas los{" "}
          <a href="#" className="text-primary hover:text-primary/80">
            Términos y Condiciones / Políticas de privacidad.
          </a>
        </span>
      </div>
      <BusyButton
        busy={isLoading}
        type="submit"
        variant="primary"
        className="mt-6"
        disabled={!isFormValid || isLoading}
      >
        Iniciar sesión
      </BusyButton>
    </form>
  );
};

export default LoginForm;
